import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { licencesApi, errorOf, TYPE_LABEL } from '../../lib/licences';
import { useConfirm } from '../ConfirmDialog';
import './Licences.css';

/**
 * LICENCES ON A JOINER OR LEAVER CHECKLIST (113 + 119), from the kit the
 * server sends administrators (lib/people.js kit -> licences):
 *
 *   leaver   the seats still theirs, and "Free the seats" - every one freed
 *            in one go, said on the checklist's own record
 *   joiner   the standard licences (every joiner gets them), ticked unless
 *            already held, and "Give the standard licences"; a full one asks
 *            before going past the seats bought
 *
 * Drawn only when the kit has a licences part: never before 119, nor while
 * the Licences switch is off.
 */
export default function RunLicences({ run, licences, onChanged }) {
  const leaver = run.kind === 'leaver';
  const seats = licences?.seats || [];
  const standard = (licences?.standard || []).filter((s) => s.assignable);
  const [chosen, setChosen] = useState(() => new Set(standard.filter((s) => !s.held).map((s) => s.id)));
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState('');
  const [problem, setProblem] = useState('');
  const [confirm, confirmDialog] = useConfirm();

  const waiting = standard.filter((s) => !s.held);
  const key = waiting.map((s) => s.id).join(',');
  useEffect(() => { setChosen(new Set(key ? key.split(',') : [])); }, [key]);

  const freeAll = async () => {
    if (!(await confirm({
      title: `Free ${run.person_name}’s ${seats.length} licence seat${seats.length === 1 ? '' : 's'}?`,
      message: 'Each goes back to its licence. Their history keeps who had it and when.',
      confirmLabel: 'Free the seats'
    }))) return;
    setBusy(true); setProblem(''); setSaid('');
    try {
      const r = await licencesApi.freePerson(run.user_id, { run_id: run.id });
      setSaid(`Freed ${r.freed.length} seat${r.freed.length === 1 ? '' : 's'}: ${r.freed.map((f) => f.product).join(', ')}.`);
      await onChanged();
    } catch (e) {
      setProblem(errorOf(e, 'The seats could not be freed.'));
    }
    setBusy(false);
  };

  const give = async (allowOver = false) => {
    const ids = [...chosen];
    if (!ids.length) return;
    setBusy(true); setProblem(''); setSaid('');
    try {
      const r = await licencesApi.giveStandard(run.user_id, { run_id: run.id, licence_ids: ids, allow_over: allowOver });
      const full = r.skipped.filter((s) => s.full);
      if (full.length && !allowOver) {
        setBusy(false);
        if (r.assigned.length) await onChanged();
        const yes = await confirm({
          title: 'Some have no free seat',
          message: `${full.map((s) => s.product).join(', ')} ${full.length === 1 ? 'has' : 'have'} every seat in use. Give ${full.length === 1 ? 'it' : 'them'} anyway, past the seats bought?`,
          confirmLabel: 'Give anyway'
        });
        if (yes) {
          setChosen(new Set(full.map((s) => s.licence_id)));
          setBusy(true);
          const again = await licencesApi.giveStandard(run.user_id, { run_id: run.id, licence_ids: full.map((s) => s.licence_id), allow_over: true });
          setSaid(`Given: ${[...r.assigned, ...again.assigned].map((a) => a.product).join(', ')}.`);
        } else if (r.assigned.length) {
          setSaid(`Given: ${r.assigned.map((a) => a.product).join(', ')}.`);
        }
      } else {
        const other = r.skipped.filter((s) => !s.full);
        setSaid(`${r.assigned.length ? `Given: ${r.assigned.map((a) => a.product).join(', ')}.` : 'Nothing was given.'}`
          + `${other.length ? ` Not given: ${other.map((s) => `${s.product} (${s.reason})`).join('; ')}.` : ''}`);
      }
      await onChanged();
    } catch (e) {
      setProblem(errorOf(e, 'The licences could not be given.'));
    }
    setBusy(false);
  };

  const toggle = (id) => setChosen((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <section className="pp-card lic-run" aria-label="Licences" data-testid="run-licences">
      <h2 className="pp-card-title">Licences</h2>
      {seats.length > 0 ? (
        <>
          <h3 className="pp-sub">{leaver ? 'Seats still theirs' : 'Seats they hold'}</h3>
          <ul className="lic-seats" aria-label="Licence seats">
            {seats.map((s) => (
              <li key={s.assignment_id} data-licence={s.id}>
                <span className="lic-seat-who">
                  <Link to={`/assets/licences/${s.id}`}>{s.product}</Link>
                  <span className="lic-seat-sub">{[s.vendor, TYPE_LABEL[s.licence_type]].filter(Boolean).join(' · ')}</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="pp-quiet" data-testid="run-licences-none">{leaver ? 'No licence seats left in their name.' : 'No licence seats yet.'}</p>
      )}

      {leaver && seats.length > 0 && (
        <div className="lic-run-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={freeAll}>
            {busy ? 'Freeing…' : `Free the seats (${seats.length})`}
          </button>
        </div>
      )}

      {!leaver && (
        standard.length === 0 ? (
          <p className="pp-help">No licence is marked for every joiner. Mark one on its page (Asset Portal, Licences) to offer it here.</p>
        ) : (
          <>
            <h3 className="pp-sub">Standard licences</h3>
            <ul className="lic-standard" aria-label="Standard licences">
              {standard.map((s) => (
                <li key={s.id}>
                  <label>
                    <input type="checkbox" disabled={s.held || busy} checked={s.held || chosen.has(s.id)} onChange={() => toggle(s.id)} />
                    <span>{s.product}</span>
                  </label>
                  {s.held ? <span className="as-chip" data-tone="success">Given</span>
                    : s.seats_free !== null && s.seats_free <= 0
                      ? <span className="as-chip" data-tone="warning">No seat free</span>
                      : s.seats_free !== null ? <span className="pp-quiet">{`${s.seats_free} free`}</span> : null}
                </li>
              ))}
            </ul>
            {waiting.length > 0 && (
              <div className="lic-run-actions">
                <button type="button" className="btn btn-primary" disabled={busy || chosen.size === 0} onClick={() => give(false)}>
                  {busy ? 'Giving…' : 'Give the standard licences'}
                </button>
              </div>
            )}
          </>
        )
      )}
      {said && <p className="pp-help" role="status">{said}</p>}
      {problem && <p className="as-error" role="alert">{problem}</p>}
      <p className="pp-help"><Link to={`/assets/licences/people/${run.user_id}`}>All their licences →</Link></p>
      {confirmDialog}
    </section>
  );
}
